import React, { Suspense } from 'react';
import { Toaster as ShadToaster } from "@/components/ui/toaster";

// Retry wrapper for lazy imports to handle stale chunks after deployment
function lazyRetry<T extends React.ComponentType<any>>(
  factory: () => Promise<{ default: T }>,
  retries = 2
): React.LazyExoticComponent<T> {
  return React.lazy(() =>
    factory().catch((err) => {
      if (retries > 0) {
        // Force reload on chunk load failure (stale deployment)
        const hasReloaded = sessionStorage.getItem('chunk_reload');
        if (!hasReloaded) {
          sessionStorage.setItem('chunk_reload', '1');
          window.location.reload();
        }
        sessionStorage.removeItem('chunk_reload');
      }
      throw err;
    })
  );
}
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider } from "./contexts/AuthContext";
import { usePageTracking } from "./hooks/usePageTracking";
import { useAuthQueryInvalidation } from "./hooks/useAuthQueryInvalidation";
import { useAuthPrefetch } from "./hooks/useAuthPrefetch";
import { ErrorBoundary } from "./components/ErrorBoundary";
import { HelmetProvider } from "react-helmet-async";
import UTMTracker from "@/components/UTMTracker";
import AuthRedirect from "./components/auth/AuthRedirect";
import AuthHashRedirect from "./components/auth/AuthHashRedirect";
import LoadingSpinner from "./components/LoadingSpinner";
import ProtectedRoute from "./components/auth/ProtectedRoute";
import FeedbackFab from "./components/feedback/FeedbackFab";
import { preloadCoreConversionRoutes } from "./utils/routePreload";

// i18n instance for provider
import { I18nextProvider } from 'react-i18next';
import i18n from './i18n/config';

// Eagerly loaded (landing - needed immediately)
import Index from "./pages/Index";

// Auth pages - lazy loaded (not needed on initial landing)
const ClientAuth = lazyRetry(() => import("./pages/ClientAuth"));
const PractitionerAuth = lazyRetry(() => import("./pages/PractitionerAuth"));

// Lazy loaded pages with retry for stale chunk recovery
const BecomePractitioner = lazyRetry(() => import("./pages/BecomePractitioner"));
const Practitioners = lazyRetry(() => import("./pages/Practitioners"));
const BookingDatetime = lazyRetry(() => import("./pages/BookingDateTime"));
const Payment = lazyRetry(() => import("./pages/Payment"));
const BookingSuccess = lazyRetry(() => import("./pages/BookingSuccess"));
const PractitionerDashboard = lazyRetry(() => import("./pages/PractitionerDashboard"));
const AdminLogin = lazyRetry(() => import("./pages/admin/AdminLogin"));
const AdminRoutes = lazyRetry(() => import("./routes/adminRoutes"));
const PaymentCancelled = lazyRetry(() => import("./pages/PaymentCancelled"));

const ClientDashboard = lazyRetry(() => import("./pages/ClientDashboard"));
const ForgotPassword = lazyRetry(() => import("./pages/ForgotPassword"));
const ResetPassword = lazyRetry(() => import("./pages/ResetPassword"));
const UpdatePassword = lazyRetry(() => import("./pages/UpdatePassword"));
const VerifyRecovery = lazyRetry(() => import("./pages/VerifyRecovery"));
const NewsEvents = lazyRetry(() => import("./pages/NewsEvents"));
const NewsArticle = lazyRetry(() => import("./pages/NewsArticle"));
const CertificationsList = lazyRetry(() => import("./pages/CertificationsList"));
const TherapyWikipedia = lazyRetry(() => import("./pages/TherapyWikipedia"));
const TherapyKnowledgePage = lazyRetry(() => import("./pages/TherapyKnowledgePage"));
const PrivacyPolicy = lazyRetry(() => import("./pages/PrivacyPolicy"));
const TermsOfService = lazyRetry(() => import("./pages/TermsOfService"));
const ZoomOAuthCallback = lazyRetry(() => import("./pages/ZoomOAuthCallback"));
const GoogleCalendarOAuthCallback = lazyRetry(() => import("./pages/GoogleCalendarOAuthCallback"));
const PractitionerDetail = lazyRetry(() => import("./components/practitioners/PractitionerDetail"));
const SessionToolsPage = lazyRetry(() => import("./pages/SessionToolsPage"));
const SmsTest = lazyRetry(() => import("./pages/SmsTest"));
const CareersPage = lazyRetry(() => import("./pages/CareersPage"));
const AccessibilityPage = lazyRetry(() => import("./pages/AccessibilityPage"));
const BookingChoice = lazyRetry(() => import("./pages/BookingChoice"));
const CrisisResources = lazyRetry(() => import("./pages/CrisisResources"));
const ServiceSelection = lazyRetry(() => import("./pages/ServiceSelection"));
const ReferralDebugPage = lazyRetry(() => import("./pages/ReferralDebugPage"));
const Events = lazyRetry(() => import("./pages/Events"));
const EventDetail = lazyRetry(() => import("./pages/EventDetail"));
const EventOrganizer = lazyRetry(() => import("./pages/EventOrganizer"));
const EventsTopic = lazyRetry(() => import("./pages/EventsTopic"));
const SmsConsent = lazyRetry(() => import("./pages/SmsConsent"));
const StudioManagerPage = lazyRetry(() => import("./pages/StudioManagerPage"));
const Community = lazyRetry(() => import("./pages/Community"));
const CommunityPost = lazyRetry(() => import("./pages/CommunityPost"));
const CommunityTopic = lazyRetry(() => import("./pages/CommunityTopic"));
const CommunityProfile = lazyRetry(() => import("./pages/CommunityProfile"));
const NotFound = lazyRetry(() => import("./pages/NotFound"));
// ConsciousnessDemo: experimental sandbox for deep analysis testing / prompt experimentation. Kept accessible at /consciousness-demo.
const ConsciousnessDemo = lazyRetry(() => import("./pages/ConsciousnessDemo"));

// Space rental MVP pages
const SpaceLanding = lazyRetry(() => import("./pages/space/SpaceLanding"));
const SpaceAuth = lazyRetry(() => import("./pages/space/SpaceAuth"));
const SpaceDashboard = lazyRetry(() => import("./pages/space/SpaceDashboard"));
const SpaceBrowse = lazyRetry(() => import("./pages/space/SpaceBrowse"));
const SpaceDetail = lazyRetry(() => import("./pages/space/SpaceDetail"));
const SpaceBookingSuccess = lazyRetry(() => import("./pages/space/SpaceBookingSuccess"));

// SessionFunctionPage uses named export, wrap it
const SessionFunctionPage = lazyRetry(() => 
  import("./components/session").then(mod => ({ default: mod.SessionFunctionPage }))
);

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
      staleTime: 30 * 1000, // 30s global default (dynamic tier)
      gcTime: 10 * 60 * 1000, // 10 min garbage collection
    },
  },
});

function AppWithProviders() {
  // React version logged once in dev only
  if (import.meta.env.DEV) console.debug('React', React.version);
  const [mounted, setMounted] = React.useState(false);
  React.useEffect(() => setMounted(true), []);
  
  return (
    <ErrorBoundary>
      <HelmetProvider>
        <I18nextProvider i18n={i18n}>
          <QueryClientProvider client={queryClient}>
            <AuthProvider>
              <div className="min-h-screen w-full">
                <div className="min-h-screen pb-safe">
                  <BrowserRouter>
                    <ErrorBoundary>
                      {mounted && <ShadToaster />}
                      <AppRoutes />
                    </ErrorBoundary>
                  </BrowserRouter>
                </div>
              </div>
            </AuthProvider>
          </QueryClientProvider>
        </I18nextProvider>
      </HelmetProvider>
    </ErrorBoundary>
  );
}

function AppRoutes() {
  usePageTracking();
  useAuthQueryInvalidation();
  useAuthPrefetch();
  const location = useLocation();
  React.useEffect(() => {
    preloadCoreConversionRoutes();
  }, []);
  return (
    <>
      <UTMTracker />
      <AuthHashRedirect />
      <FeedbackFab />
      <ErrorBoundary key={location.pathname}>
      <Suspense fallback={<LoadingSpinner />}>
        <Routes>
          <Route path="/" element={<Index />} />
          <Route path="/client-auth" element={<ClientAuth />} />
          <Route path="/signup" element={<ClientAuth />} />
          <Route path="/practitioner-auth" element={<PractitionerAuth />} />
          <Route path="/become-practitioner" element={<BecomePractitioner />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/update-password" element={<UpdatePassword />} />
          <Route path="/verify-recovery" element={<VerifyRecovery />} />
          <Route path="/booking-choice" element={<BookingChoice />} />
          <Route path="/crisis-resources" element={<CrisisResources />} />
          <Route path="/service-selection" element={<ServiceSelection />} />
          <Route path="/book-an-appointment" element={<Practitioners />} />
          <Route path="/book-an-appointment/:id" element={<PractitionerDetail isFullPage={true} />} />
          <Route path="/framework" element={<TherapyWikipedia />} />
          <Route path="/framework/:therapyId" element={<TherapyKnowledgePage />} />
          <Route path="/certifications-legacy" element={<CertificationsList />} />
          <Route path="/booking-datetime" element={<BookingDatetime />} />
          <Route path="/payment" element={<Payment />} />
          <Route path="/booking-success" element={<BookingSuccess />} />
          <Route path="/practitioner-dashboard" element={<ProtectedRoute requiredRole={"practitioner"} element={<PractitionerDashboard />} />} />
          <Route path="/client-dashboard" element={<ProtectedRoute requiredRole={"client"} element={<ClientDashboard />} />} />
          <Route path="/session/:bookingId" element={<SessionFunctionPage />} />
          <Route path="/session-tools/:bookingId" element={<ProtectedRoute requiredRole={"practitioner"} element={<SessionToolsPage />} />} />
          <Route path="/admin" element={<AdminLogin />} />
          <Route path="/admin/*" element={<ProtectedRoute requiredRole={"admin"} element={<AdminRoutes />} />} />
          <Route path="/payment-cancelled" element={<PaymentCancelled />} />
          
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/terms-of-service" element={<TermsOfService />} />
          <Route path="/zoom/callback" element={<ZoomOAuthCallback />} />
          <Route path="/oauth/google-calendar/callback" element={<GoogleCalendarOAuthCallback />} />
          <Route path="/careers" element={<CareersPage />} />
          <Route path="/accessibility" element={<AccessibilityPage />} />
          <Route path="/news-events" element={<NewsEvents />} />
          <Route path="/news/:slug" element={<NewsArticle />} />
          <Route path="/dashboard" element={<AuthRedirect />} />
          <Route path="/profile" element={<AuthRedirect />} />
          <Route path="/referral-debug" element={<ReferralDebugPage />} />
          <Route path="/sms-test" element={<SmsTest />} />
          <Route path="/events" element={<Events />} />
          <Route path="/events/topic/:slug" element={<EventsTopic />} />
          <Route path="/events/host/:id" element={<EventOrganizer />} />
          <Route path="/events/:id" element={<EventDetail />} />
          <Route path="/sms-consent" element={<SmsConsent />} />
          <Route path="/studio-manager" element={<ProtectedRoute requiredRole={"practitioner"} element={<StudioManagerPage />} />} />
          <Route path="/community" element={<Community mode="feed" />} />
          <Route path="/community/saved" element={<Community mode="saved" />} />
          <Route path="/community/explore" element={<Community mode="explore" />} />
          <Route path="/community/knowledge" element={<Community mode="knowledge" />} />
          <Route path="/community/news" element={<Community mode="news" />} />
          <Route path="/community/tag/:tag" element={<Community mode="tag" />} />
          <Route path="/community/topic/:slug" element={<CommunityTopic />} />
          <Route path="/community/post/:id" element={<CommunityPost />} />
          <Route path="/community/u/:handle" element={<CommunityProfile />} />
          <Route path="/certifications" element={<Navigate to="/framework" replace />} />
          {/* Consciousness demo — experimental sandbox, kept accessible (not linked from main nav). */}
          <Route path="/consciousness-demo" element={<ConsciousnessDemo />} />
          {/* Space rental MVP */}
          <Route path="/space" element={<SpaceLanding />} />
          <Route path="/space/auth" element={<SpaceAuth />} />
          <Route path="/space/dashboard" element={<ProtectedRoute requiredRole={"host"} element={<SpaceDashboard />} />} />
          <Route path="/space/browse" element={<SpaceBrowse />} />
          <Route path="/space/browse/:id" element={<SpaceDetail />} />
          <Route path="/space/booking-success" element={<SpaceBookingSuccess />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </Suspense>
      </ErrorBoundary>
    </>
  );
}

export default AppWithProviders;
